import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth, MFAChallengeData } from './contexts/AuthContext';
import MFAChallengeModal from './components/auth/MFAChallengeModal';
import { TenantProvider } from './contexts/TenantContext';
import { ChatProvider } from './contexts/ChatContext';
import { SessionTimeoutProvider } from './providers/SessionTimeoutProvider';
import { TenantBrandingProvider } from './components/TenantBrandingProvider';
import { TenantNavigationProvider } from './components/TenantNavigationProvider';
import { AdminContextProvider } from './contexts/AdminContextProvider';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Unified Portal (WQ-076)
import Portal from './components/Portal';

// Styles
import './styles/design-system.css';
import './styles/components.css';
import './styles/layout.css';
import './styles/admin-portal.css';

interface LocationState {
  from?: { pathname?: string };
}

const AppContent: React.FC = () => {
  const { isAuthenticated, user, login, loginWithTokens, logout, sessionEndedMessage, clearSessionEndedMessage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [authError, setAuthError] = useState<string | null>(null);

  // MFA challenge state (WQ-067)
  const [mfaChallenge, setMfaChallenge] = useState<MFAChallengeData | null>(null);
  const [mfaRememberMe, setMfaRememberMe] = useState(false);

  const redirectAfterAuth = useCallback(() => {
    const state = location.state as LocationState | null;
    const target = state?.from?.pathname && state.from.pathname !== '/login' && state.from.pathname !== '/'
      ? state.from.pathname
      : '/dashboard';
    navigate(target, { replace: true });
  }, [navigate, location.state]);

  const handleLogin = async (email: string, password: string, rememberMe: boolean = false) => {
    const result = await login(email, password, rememberMe);

    if (typeof result === 'object' && result.mfa_required) {
      // MFA challenge — show modal
      setMfaChallenge(result);
      setMfaRememberMe(rememberMe);
      return;
    }

    if (result === true) {
      setAuthError(null);
      redirectAfterAuth();
    } else {
      setAuthError('Login failed. Please check your credentials.');
    }
  };

  const handleMfaSuccess = (tokenResponse: { access_token: string; refresh_token: string; token_type: string; expires_in: number; user: Record<string, unknown>; recovery_codes_remaining?: number }) => {
    loginWithTokens(
      tokenResponse,
      mfaRememberMe
    );
    setMfaChallenge(null);
    redirectAfterAuth();
  };

  const handleMfaCancel = () => {
    setMfaChallenge(null);
  };

  const handleRegister = async (name: string, email: string, password: string) => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });

      if (response.ok) {
        const success = await login(email, password, false);
        if (success) {
          navigate('/dashboard', { replace: true });
        }
      } else {
        const error = await response.json();
        setAuthError(error.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Register error:', error);
      setAuthError('Registration failed. Please try again.');
    }
  };

  const handleLogout = useCallback(() => {
    logout();
    navigate('/', { replace: true });
  }, [logout, navigate]);

  return (
    <div className="app">
      <div className="noise-overlay" />
      <Routes>
        {/* WQ-334: the marketing landing page is archived (_ARCHIVE/platform-ui/) — unauthenticated `/`
             goes straight to the login card (keeps the MFA modal mount on /login).
             WQ-335: the redirect MUST carry the query string. The SSO callback returns to
             `/?sso_code=…` (or `?sso_error=…`), and AuthContext's mount-time restoreSession reads
             window.location.search — a bare `/login` strips the code before it is exchanged and
             the user lands back on the login screen unauthenticated. */}
        <Route
          path="/"
          element={
            isAuthenticated
              ? <Navigate to="/dashboard" replace />
              : <Navigate to={{ pathname: '/login', search: location.search }} replace />
          }
        />
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <>
                {/* WQ-342 F2: a session the server ended at refresh lands here with its reason */}
                <LoginPage
                  onLogin={handleLogin}
                  error={authError ?? sessionEndedMessage}
                  onErrorClear={() => { setAuthError(null); clearSessionEndedMessage(); }}
                />
                {mfaChallenge && (
                  <MFAChallengeModal
                    mfaToken={mfaChallenge.mfa_token}
                    mfaMethods={mfaChallenge.mfa_methods}
                    onSuccess={handleMfaSuccess}
                    onCancel={handleMfaCancel}
                  />
                )}
              </>
            )
          }
        />
        <Route
          path="/register"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RegisterPage
                onRegister={handleRegister}
                onNavigateToLogin={() => navigate('/login')}
                error={authError}
                onErrorClear={() => setAuthError(null)}
              />
            )
          }
        />
        {/* WQ-261: alias /account-requests → /requests (operator UX —
             API namespace and SPA route differ; operators who type the
             API path expecting it to match were bouncing to /dashboard
             via Portal's default-tab fallback). */}
        <Route path="/account-requests" element={<Navigate to="/requests" replace />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Portal
                onLogout={handleLogout}
                userEmail={user?.email}
                userName={user?.name}
                userRole={user?.role}
              />
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    // BrowserRouter provides the react-router-dom context that
    // BICommandCenterPage's useSearchParams() requires for the WQ-253
    // ?tab= deep-link reverse-lookup. Without it, the page crashes at
    // mount and ContentErrorBoundary shows "Something went wrong".
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SessionTimeoutProvider>
            <TenantProvider>
              <TenantBrandingProvider defaultAppName="AI Platform">
                <TenantNavigationProvider>
                  <AdminContextProvider>
                    <ChatProvider>
                      <AppContent />
                    </ChatProvider>
                  </AdminContextProvider>
                </TenantNavigationProvider>
              </TenantBrandingProvider>
            </TenantProvider>
          </SessionTimeoutProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
